import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Mail, Phone, Lock } from 'lucide-react';
import { InputField } from '../components/InputField';
import { Button } from '../components/Button';
import { registerUser } from '../api/auth';
import { useAuth } from '../store/authStore';
export function RegisterScreen() {
  const navigate = useNavigate();
  const {
    login
  } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.fullName || formData.fullName.length < 2) {
      newErrors.fullName = 'Name must be at least 2 characters';
    }
    if (!formData.email || !/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (formData.phone && formData.phone.length < 10) {
      newErrors.phone = 'Phone number must be at least 10 digits';
    }
    if (!formData.password || formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: ''
      });
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!validate()) return;
    setIsLoading(true);
    try {
      const response = await registerUser({
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        password: formData.password
      });
      login(response.token, response.user);
      navigate('/app/dashboard');
    } catch (err: any) {
      setError(err.message || 'Failed to create account');
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="min-h-screen w-full bg-background md:bg-secondary flex flex-col items-center justify-center p-0 md:p-6 animate-fadeInUp" data-id="element-2055">
      <div className="w-full max-w-[480px] min-h-screen md:min-h-0 bg-background md:rounded-card md:shadow-elevated flex flex-col relative" data-id="element-2056">
        {error && <div className="absolute top-0 left-0 right-0 bg-danger text-white p-4 text-center text-sm font-medium animate-slideDown z-20 md:rounded-t-card" data-id="element-2057">
            {error}
          </div>}

        <div className="p-6 pt-12 flex-1 overflow-y-auto" data-id="element-2058">
          <button onClick={() => navigate('/intro')} className="mb-8 text-text-secondary hover:text-text-primary transition-colors" aria-label="Go back" data-id="element-2059">
            <ArrowLeft size={24} data-id="element-2060" />
          </button>

          <div className="mb-8" data-id="element-2061">
            <h1 className="text-[28px] font-semibold text-text-primary mb-2" data-id="element-2062">
              Create your account
            </h1>
            <p className="text-[16px] text-text-secondary" data-id="element-2063">
              Join Aurora Care and start monitoring
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-id="element-2064">
            <label htmlFor="fullName" className="sr-only" data-id="element-2065">
              Full Name
            </label>
            <InputField id="fullName" name="fullName" placeholder="Your full name" icon={<User size={20} data-id="element-2067" />} value={formData.fullName} onChange={handleChange} error={errors.fullName} data-id="element-2066" />
            <label htmlFor="email" className="sr-only" data-id="element-2068">
              Email
            </label>
            <InputField id="email" name="email" type="email" placeholder="your@email.com" icon={<Mail size={20} data-id="element-2070" />} value={formData.email} onChange={handleChange} error={errors.email} data-id="element-2069" />
            <label htmlFor="phone" className="sr-only" data-id="element-2071">
              Phone
            </label>
            <InputField id="phone" name="phone" type="tel" placeholder="+234 800 000 0000" icon={<Phone size={20} data-id="element-2073" />} value={formData.phone} onChange={handleChange} error={errors.phone} data-id="element-2072" />
            <label htmlFor="password" className="sr-only" data-id="element-2074">
              Password
            </label>
            <InputField id="password" name="password" type="password" placeholder="Create a password" icon={<Lock size={20} data-id="element-2076" />} value={formData.password} onChange={handleChange} error={errors.password} data-id="element-2075" />
            <label htmlFor="confirmPassword" className="sr-only" data-id="element-2077">
              Confirm Password
            </label>
            <InputField id="confirmPassword" name="confirmPassword" type="password" placeholder="Confirm password" icon={<Lock size={20} data-id="element-2079" />} value={formData.confirmPassword} onChange={handleChange} error={errors.confirmPassword} data-id="element-2078" />

            <div className="pt-4 pb-8" data-id="element-2080">
              <Button type="submit" isLoading={isLoading} loadingText="Creating account..." data-id="element-2081">
                Create Account
              </Button>

              <div className="mt-6 text-center" data-id="element-2082">
                <button type="button" onClick={() => navigate('/login')} className="text-primary font-medium text-[14px] hover:opacity-80 transition-opacity" data-id="element-2083">
                  Already have an account? Sign in
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>;
}